import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useNavigate, Navigate, Link } from "react-router-dom";

const GetStartedPage = () => {
  const [loading, setLoading] = useState(false);
  const { user, loading: authLoading, role, signUp } = useAuth();
  const navigate = useNavigate();

  // New user form state if not authenticated
  const [authEmail, setAuthEmail] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [authName, setAuthName] = useState("");

  // If already logged in, redirect to dashboard / onboarding
  if (!authLoading && user) {
    return <Navigate to="/dashboard/onboarding" replace />;
  }

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  const handleCreateCompany = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData(e.currentTarget);
    const companyName = formData.get("companyName") as string;
    const gstin = formData.get("gstin") as string;
    const phone = formData.get("phone") as string;
    const city = formData.get("city") as string;
    const state = formData.get("state") as string;
    const address = formData.get("address") as string;

    try {
      let currentUserId = user?.id;

      // If not logged in yet, register the user first
      if (!currentUserId) {
        if (!authEmail || !authPassword || !authName) {
          toast.error("Please fill in account details");
          setLoading(false);
          return;
        }

        const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
          email: authEmail,
          password: authPassword,
          options: {
            data: { name: authName, role: "company_admin" },
          },
        });

        if (signUpError || !signUpData?.user) {
          throw new Error(signUpError?.message || "Failed to create user account");
        }
        currentUserId = signUpData.user.id;
      }

      // 1. Insert company
      const { data: company, error: companyError } = await supabase
        .from("companies")
        .insert({
          name: companyName,
          gstin: gstin || null,
          phone: phone || null,
          city: city || null,
          state: state || null,
          address: address || null,
          created_by: currentUserId,
        })
        .select("id")
        .single();

      if (companyError) throw companyError;

      // 2. Update profile and user with company_id
      await supabase.from("profiles").update({ company_id: company.id }).eq("id", currentUserId);
      await supabase.from("users").update({ company_id: company.id }).eq("id", currentUserId);

      // 3. Link into company_members
      const memberName = user?.name || authName || "Owner";
      const memberEmail = user?.email || authEmail || "";
      await supabase.from("company_members").insert({
        company_id: company.id,
        user_id: currentUserId,
        user_name: memberName,
        email: memberEmail,
        role: "owner",
      });

      // Update cached session
      const savedUser = localStorage.getItem("accoupro_auth_user");
      if (savedUser) {
        try {
          const parsed = JSON.parse(savedUser);
          parsed.company = company.id;
          parsed.company_id = company.id;
          localStorage.setItem("accoupro_auth_user", JSON.stringify(parsed));
        } catch {}
      }

      toast.success("Company created! Redirecting to dashboard...");
      window.location.assign("/dashboard");
    } catch (error: any) {
      toast.error(error.message || "Failed to create company");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-lg shadow-lg border-border">
        <CardHeader className="text-center">
          <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center mx-auto mb-2 text-primary-foreground shadow-soft">
            <Building2 className="h-6 w-6" />
          </div>
          <CardTitle className="text-2xl font-bold font-display">Set Up Your Company</CardTitle>
          <CardDescription>
            {user ? `Logged in as ${user.email}. Enter your company details to get started.` : "Create your account and company to start using AccouPro"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreateCompany} className="space-y-4">
            {!user && (
              <div className="p-4 border border-border bg-muted/40 rounded-xl space-y-3 mb-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Admin Account Details</p>
                <div>
                  <Label htmlFor="authName">Your Full Name *</Label>
                  <Input
                    id="authName"
                    value={authName}
                    onChange={(e) => setAuthName(e.target.value)}
                    required
                    placeholder="e.g. Rajesh Kumar"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="authEmail">Email Address *</Label>
                    <Input
                      id="authEmail"
                      type="email"
                      value={authEmail}
                      onChange={(e) => setAuthEmail(e.target.value)}
                      required
                      placeholder="you@company.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="authPassword">Password *</Label>
                    <Input
                      id="authPassword"
                      type="password"
                      value={authPassword}
                      onChange={(e) => setAuthPassword(e.target.value)}
                      required
                      placeholder="••••••••"
                    />
                  </div>
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="companyName">Company Name *</Label>
              <Input id="companyName" name="companyName" required placeholder="e.g. Acme Enterprises Pvt Ltd" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="gstin">GSTIN (Optional)</Label>
                <Input id="gstin" name="gstin" placeholder="e.g. 27AABCA1234A1Z5" />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input id="phone" name="phone" type="tel" placeholder="e.g. 9876543210" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="city">City</Label>
                <Input id="city" name="city" placeholder="e.g. Mumbai" />
              </div>
              <div>
                <Label htmlFor="state">State</Label>
                <Input id="state" name="state" placeholder="e.g. Maharashtra" />
              </div>
            </div>
            <div>
              <Label htmlFor="address">Address</Label>
              <Input id="address" name="address" placeholder="Office address" />
            </div>
            <Button type="submit" disabled={loading} className="w-full h-11 text-sm font-semibold">
              {loading ? "Creating Company..." : "Create Company & Launch AccouPro"}
            </Button>
          </form>

          {!user && (
            <p className="text-center text-xs text-muted-foreground mt-4">
              Already have an account?{" "}
              <Link to="/login" className="text-primary hover:underline font-medium">
                Sign In
              </Link>
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default GetStartedPage;
